import {
  Box,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../../DashboardContext';
import { useAppSocket } from '../../../../providers/AppSocketContext';
import { useAuth } from '../../../../hooks/useAuth';
import { canRead, canViewOperationField } from '../../../../util/access';
import { DashboardInfoTip, DashboardMetricLabel } from '../DashboardInfoTip';
import DashboardWidget, { DashboardSizedChart } from '../DashboardWidget';
import { useChartSurface } from '../../dashboardChrome';

const formatNum = (n: number, currency: string) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n) + ` ${currency}`;

const formatShort = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(Math.round(value));
};

const DeltaChip = ({ changePct }: { changePct: number }) => {
  const up = changePct > 0;
  const flat = changePct === 0;
  return (
    <Chip
      size="small"
      label={`${up ? '+' : ''}${changePct.toFixed(1)}%`}
      sx={{
        ml: 1,
        height: 22,
        fontSize: '0.7rem',
        bgcolor: flat ? 'action.selected' : up ? 'success.light' : 'error.light',
        color: flat ? 'text.secondary' : up ? 'success.dark' : 'error.dark',
      }}
    />
  );
};

const MetricTile = ({
  label,
  helpKey,
  value,
  delta,
  loading,
}: {
  label: string;
  helpKey: string;
  value: string;
  delta?: number;
  loading?: boolean;
}) => (
  <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
    {loading ? (
      <Skeleton height={60} />
    ) : (
      <>
        <DashboardMetricLabel label={label} helpKey={helpKey} />
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          <Typography variant="h6" fontWeight={700}>
            {value}
          </Typography>
          {delta !== undefined && <DeltaChip changePct={delta} />}
        </Box>
      </>
    )}
  </Paper>
);

const Section = ({
  title,
  helpKey,
  subtitle,
  chartHeight = 300,
  scrollable = false,
  children,
}: {
  title: string;
  helpKey: string;
  subtitle?: string;
  chartHeight?: number;
  scrollable?: boolean;
  children: React.ReactNode;
}) => (
  <DashboardWidget
    title={title}
    helpKey={helpKey}
    subtitle={subtitle}
    chartHeight={chartHeight}
    scrollable={scrollable}
    sx={{ p: 2.5 }}
  >
    {children}
  </DashboardWidget>
);

const SectionBody = ({
  loading,
  height,
  children,
}: {
  loading: boolean;
  height: number;
  children: React.ReactNode;
}) => (loading ? <Skeleton height={height} /> : <>{children}</>);

const AdvancedDashboard = () => {
  const { advanced, sectionLoading, currency } = useDashboard();
  const { t } = useTranslation();
  const { appSettings } = useAppSocket();
  const { access } = useAuth();
  const canViewItemCost = canRead(access, 'item', 'cost_price');
  const canViewSupplyCost = canViewOperationField(access, 'supply', 'cost');
  const themeColor = appSettings?.UI_THEME_PRIMARY?.value || '#8b1010';
  const chrome = useChartSurface();
  const advancedLoading = sectionLoading.advanced;

  const kpi = advanced.kpi;
  const deltas = advanced.kpiDeltas;

  const severityColor = (s: string) =>
    s === 'high' ? 'error' : s === 'medium' ? 'warning' : 'info';

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Chip label={t('common:dashboard.advanced.badge')} color="primary" variant="outlined" />
          <DashboardInfoTip helpKey="advanced.overview" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {t('common:dashboard.advanced.description')}
        </Typography>
      </Box>

      {!advancedLoading && advanced.alerts.length > 0 && (
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t('common:dashboard.advanced.alertsTitle')}
            </Typography>
            <DashboardInfoTip helpKey="advanced.alerts" />
          </Box>
          {advanced.alerts.map((a) => {
            const key = String(a.id || '').startsWith('credit-') ? 'creditLimit' : a.id;
            const params = a.params || {};
            return (
              <Alert key={a.id} severity={severityColor(a.severity) as 'error' | 'warning' | 'info'}>
                <AlertTitle>
                  {t(`common:dashboard.alerts.${key}.title`, { defaultValue: a.title, ...params })}
                </AlertTitle>
                {t(`common:dashboard.alerts.${key}.message`, { defaultValue: a.message, ...params })}
              </Alert>
            );
          })}
        </Stack>
      )}

      <Grid container spacing={2}>
        {canViewItemCost && (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <MetricTile
              label={t('common:dashboard.advanced.marginPct')}
              helpKey="advanced.marginPct"
              value={`${kpi.marginPct.toFixed(1)}%`}
              delta={deltas.totalProfit?.changePct}
              loading={advancedLoading}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <MetricTile
            label={t('common:dashboard.advanced.grossSales')}
            helpKey="advanced.grossSales"
            value={formatNum(kpi.grossSales, currency)}
            delta={deltas.totalSales?.changePct}
            loading={advancedLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <MetricTile
            label={t('common:dashboard.advanced.returnRate')}
            helpKey="advanced.returnRate"
            value={`${kpi.returnRatePct.toFixed(1)}%`}
            loading={advancedLoading}
          />
        </Grid>
        {canViewSupplyCost && (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <MetricTile
              label={t('common:dashboard.advanced.supplyReturnRate')}
              helpKey="advanced.supplyReturnRate"
              value={`${(kpi.supplyReturnRatePct ?? 0).toFixed(1)}%`}
              loading={advancedLoading}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <MetricTile
            label={t('common:dashboard.advanced.collectionRate')}
            helpKey="advanced.collectionRate"
            value={`${kpi.collectionRatePct.toFixed(1)}%`}
            loading={advancedLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <MetricTile
            label={t('common:dashboard.advanced.pendingDepositsPct')}
            helpKey="advanced.pendingDepositsPct"
            value={`${kpi.pendingDepositsPct.toFixed(1)}%`}
            delta={deltas.pendingDeposits?.changePct}
            loading={advancedLoading}
          />
        </Grid>
        {canViewSupplyCost && (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <MetricTile
              label={t('common:dashboard.advanced.supplyToSales')}
              helpKey="advanced.supplyToSales"
              value={`${kpi.supplyToSalesRatio.toFixed(2)}x`}
              delta={deltas.totalSupplyValue?.changePct}
              loading={advancedLoading}
            />
          </Grid>
        )}
        {canViewItemCost && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <MetricTile
                label={t('common:dashboard.advanced.shrinkage')}
                helpKey="advanced.shrinkage"
                value={formatNum(kpi.shrinkageValue, currency)}
                loading={advancedLoading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <MetricTile
                label={t('common:dashboard.advanced.stockCoverage')}
                helpKey="advanced.stockCoverage"
                value={
                  kpi.stockCoverageDays != null
                    ? t('common:dashboard.advanced.days', { count: kpi.stockCoverageDays })
                    : '—'
                }
                loading={advancedLoading}
              />
            </Grid>
          </>
        )}
        {canViewSupplyCost && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <MetricTile
                label={t('common:dashboard.advanced.supplierPaymentRate')}
                helpKey="advanced.supplierPaymentRate"
                value={`${kpi.supplierPaymentRatePct.toFixed(1)}%`}
                delta={deltas.totalSupplierPayments?.changePct}
                loading={advancedLoading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <MetricTile
                label={t('common:dashboard.advanced.openPayables')}
                helpKey="advanced.openPayables"
                value={formatNum(kpi.openPayablesValue, currency)}
                delta={deltas.supplierBalanceOwed?.changePct}
                loading={advancedLoading}
              />
            </Grid>
          </>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <MetricTile
            label={t('common:dashboard.advanced.walletCredits')}
            helpKey="advanced.walletCredits"
            value={formatNum(deltas.walletCredits?.current ?? 0, currency)}
            delta={deltas.walletCredits?.changePct}
            loading={advancedLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <MetricTile
            label={t('common:dashboard.advanced.walletDebits')}
            helpKey="advanced.walletDebits"
            value={formatNum(deltas.walletDebits?.current ?? 0, currency)}
            delta={deltas.walletDebits?.changePct}
            loading={advancedLoading}
          />
        </Grid>
        {canViewSupplyCost && (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <MetricTile
              label={t('common:dashboard.advanced.overdueSupplies')}
              helpKey="advanced.overdueSupplies"
              value={String(kpi.overdueSupplyCount)}
              loading={advancedLoading}
            />
          </Grid>
        )}
      </Grid>

      <Section title={t('common:dashboard.advanced.dualTrend')} helpKey="advanced.dualTrend" subtitle={t('common:dashboard.advanced.dualTrendHint')} chartHeight={300}>
        {advancedLoading ? (
          <Skeleton height={300} />
        ) : advanced.dualTrend.length > 0 ? (
          <DashboardSizedChart height={300}>
            <ComposedChart data={advanced.dualTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke={chrome.grid} />
              <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={11} />
              <YAxis tickFormatter={formatShort} fontSize={11} />
              <Tooltip formatter={(v: number) => formatNum(v, currency)} />
              <Legend />
              <Bar dataKey="paymentAmount" name={t('common:dashboard.advanced.payments')} fill="#0288d1" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="salesAmount" name={t('common:dashboard.advanced.sales')} stroke={themeColor} strokeWidth={2} dot={false} />
            </ComposedChart>
          </DashboardSizedChart>
        ) : (
          <Typography color="text.secondary">{t('common:dashboard.noData')}</Typography>
        )}
      </Section>



      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Section title={t('common:dashboard.advanced.returnsTrend')} helpKey="advanced.returnsTrend" chartHeight={240}>
            <SectionBody loading={advancedLoading} height={240}>
              {advanced.salesReturnTrend.length > 0 ? (
                <DashboardSizedChart height={240}>
                  <AreaChart data={advanced.salesReturnTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={11} />
                    <YAxis tickFormatter={formatShort} />
                    <Tooltip formatter={(v: number) => formatNum(v, currency)} />
                    <Area type="monotone" dataKey="grossSales" stackId="1" stroke="#2e7d32" fill="#2e7d3288" name={t('common:dashboard.advanced.grossSales')} />
                    <Area type="monotone" dataKey="returnAmount" stackId="2" stroke="#d32f2f" fill="#d32f2f55" name={t('common:dashboard.advanced.returns')} />
                  </AreaChart>
                </DashboardSizedChart>
              ) : (
                <Typography color="text.secondary">{t('common:dashboard.noData')}</Typography>
              )}
            </SectionBody>
          </Section>
        </Grid>
        {canViewSupplyCost && (
          <Grid size={{ xs: 12, lg: 6 }}>
            <Section title={t('common:dashboard.advanced.supplyReturnsTrend')} helpKey="advanced.supplyReturnsTrend" chartHeight={240}>
              <SectionBody loading={advancedLoading} height={240}>
                {(advanced.supplyReturnTrend || []).length > 0 ? (
                  <DashboardSizedChart height={240}>
                    <AreaChart data={advanced.supplyReturnTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={11} />
                      <YAxis tickFormatter={formatShort} />
                      <Tooltip formatter={(v: number) => formatNum(v, currency)} />
                      <Area type="monotone" dataKey="grossSupplies" stackId="1" stroke="#0288d1" fill="#0288d188" name={t('common:dashboard.advanced.grossSupplies')} />
                      <Area type="monotone" dataKey="returnAmount" stackId="2" stroke="#ed6c02" fill="#ed6c0255" name={t('common:dashboard.advanced.supplyReturns')} />
                    </AreaChart>
                  </DashboardSizedChart>
                ) : (
                  <Typography color="text.secondary">{t('common:dashboard.noData')}</Typography>
                )}
              </SectionBody>
            </Section>
          </Grid>
        )}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Section title={t('common:dashboard.advanced.dayOfWeek')} helpKey="advanced.dayOfWeek" subtitle={t('common:dashboard.advanced.dayOfWeekHint')} chartHeight={240}>
            <SectionBody loading={advancedLoading} height={240}>
              {advanced.salesHeatmap.some((d) => d.amount > 0) ? (
                <DashboardSizedChart height={240}>
                  <BarChart data={advanced.salesHeatmap}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis tickFormatter={formatShort} />
                    <Tooltip formatter={(v: number) => formatNum(v, currency)} />
                    <Bar dataKey="amount" fill={themeColor} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </DashboardSizedChart>
              ) : (
                <Typography color="text.secondary">{t('common:dashboard.noData')}</Typography>
              )}
            </SectionBody>
          </Section>
        </Grid>
      </Grid>

      <Section title={t('common:dashboard.advanced.forecast')} helpKey="advanced.forecast" subtitle={t('common:dashboard.advanced.forecastHint')} chartHeight={260}>
        <SectionBody loading={advancedLoading} height={260}>
          {advanced.salesForecast.length > 0 ? (
            <DashboardSizedChart height={260}>
              <ComposedChart data={advanced.salesForecast}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={11} />
                <YAxis tickFormatter={formatShort} />
                <Tooltip />
                <Area type="monotone" dataKey="amount" stroke={themeColor} fill={`${themeColor}33`} name={t('common:dashboard.sales.amount')} />
                <Line type="monotone" dataKey="movingAvg" stroke="#0288d1" strokeDasharray="4 4" name={t('common:dashboard.advanced.movingAvg')} dot={false} />
              </ComposedChart>
            </DashboardSizedChart>
          ) : (
            <Typography color="text.secondary">{t('common:dashboard.noData')}</Typography>
          )}
        </SectionBody>
      </Section>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Section title={t('common:dashboard.advanced.supplierPaymentsTrend')} helpKey="advanced.supplierPaymentsTrend" chartHeight={220}>
            <SectionBody loading={advancedLoading} height={220}>
              {advanced.supplierPaymentTrend.length > 0 ? (
                <DashboardSizedChart height={220}>
                  <AreaChart data={advanced.supplierPaymentTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={11} />
                    <YAxis tickFormatter={formatShort} />
                    <Tooltip formatter={(v: number) => formatNum(v, currency)} />
                    <Area type="monotone" dataKey="amount" stroke="#5d4037" fill="#5d403733" name={t('common:dashboard.kpi.supplierPayments')} />
                  </AreaChart>
                </DashboardSizedChart>
              ) : (
                <Typography color="text.secondary">{t('common:dashboard.noData')}</Typography>
              )}
            </SectionBody>
          </Section>
        </Grid>
        {canViewSupplyCost && (
          <Grid size={{ xs: 12, lg: 6 }}>
            <Section title={t('common:dashboard.advanced.openPayablesAging')} helpKey="advanced.openPayablesAging" chartHeight={220}>
              <SectionBody loading={advancedLoading} height={220}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('common:dashboard.advanced.openPayablesSummary', {
                    count: advanced.openPayables.openPayablesCount,
                    value: formatNum(advanced.openPayables.openPayablesValue, currency),
                  })}
                </Typography>
                {advanced.openPayables.aging.some((bucket) => bucket.value > 0) ? (
                  <DashboardSizedChart height={180}>
                    <BarChart data={advanced.openPayables.aging}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="bucket" />
                      <YAxis tickFormatter={formatShort} />
                      <Tooltip formatter={(v: number) => formatNum(v, currency)} />
                      <Bar dataKey="value" fill="#5d4037" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </DashboardSizedChart>
                ) : (
                  <Typography color="text.secondary">{t('common:dashboard.noData')}</Typography>
                )}
              </SectionBody>
            </Section>
          </Grid>
        )}
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Section title={t('common:dashboard.advanced.collection')} helpKey="advanced.collection" chartHeight={200} scrollable>
            <SectionBody loading={advancedLoading} height={200}>
              <Stack spacing={1}>
                <Typography variant="body2">{t('common:dashboard.advanced.periodInvoiced')}: <strong>{formatNum(kpi.periodInvoiced, currency)}</strong></Typography>
                <Typography variant="body2">{t('common:dashboard.advanced.periodPaid')}: <strong>{formatNum(kpi.periodPaid, currency)}</strong></Typography>
                <Typography variant="body2">{t('common:dashboard.advanced.outstandingCreated')}: <strong>{formatNum(kpi.outstandingCreatedInPeriod, currency)}</strong></Typography>
                <Typography variant="body2">{t('common:dashboard.advanced.unreconciledCount')}: <strong>{kpi.unreconciledPaymentCount}</strong></Typography>
              </Stack>
              {advanced.pendingDepositsTrend.length > 0 && (
                <Box sx={{ mt: 2, flex: 1, minHeight: 140 }}>
                  <DashboardSizedChart height={140}>
                    <AreaChart data={advanced.pendingDepositsTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={10} />
                      <YAxis tickFormatter={formatShort} fontSize={10} />
                      <Tooltip formatter={(v: number) => formatNum(v, currency)} />
                      <Area type="monotone" dataKey="amount" stroke="#ed6c02" fill="#ed6c0233" name={t('common:dashboard.kpi.pendingDeposits')} />
                    </AreaChart>
                  </DashboardSizedChart>
                </Box>
              )}
            </SectionBody>
          </Section>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Section title={t('common:dashboard.advanced.openInvoices')} helpKey="advanced.openInvoices" chartHeight={220}>
            <SectionBody loading={advancedLoading} height={220}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('common:dashboard.advanced.openInvoiceSummary', {
                  count: advanced.openInvoices.openInvoiceCount,
                  value: formatNum(advanced.openInvoices.openInvoiceValue, currency),
                })}
              </Typography>
              {advanced.openInvoices.aging.length > 0 ? (
                <DashboardSizedChart height={180}>
                  <BarChart data={advanced.openInvoices.aging}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" />
                    <YAxis tickFormatter={formatShort} />
                    <Tooltip formatter={(v: number) => formatNum(v, currency)} />
                    <Bar dataKey="value" fill="#ed6c02" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </DashboardSizedChart>
              ) : (
                <Typography color="text.secondary">{t('common:dashboard.noData')}</Typography>
              )}
            </SectionBody>
          </Section>
        </Grid>
      </Grid>

      <Section title={t('common:dashboard.advanced.inventoryHealth')} helpKey="advanced.inventoryHealth" chartHeight={220} scrollable>
        <SectionBody loading={advancedLoading} height={220}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('common:dashboard.advanced.outOfStock')}: <strong>{kpi.outOfStockCount}</strong>
          </Typography>
          {advanced.inventoryHealth.overstockItems.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common:dashboard.inventory.stockByDepot')}</TableCell>
                  <TableCell>{t('common:dashboard.topItems.title')}</TableCell>
                  <TableCell align="right">{t('common:dashboard.topItems.qty')}</TableCell>
                  {canViewItemCost && (
                    <TableCell align="right">{t('common:dashboard.inventory.value')}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {advanced.inventoryHealth.overstockItems.slice(0, 8).map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.depot}</TableCell>
                    <TableCell>{row.item}</TableCell>
                    <TableCell align="right">{row.stock}</TableCell>
                    {canViewItemCost && (
                      <TableCell align="right">{formatNum(row.value, currency)}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography color="text.secondary">{t('common:dashboard.advanced.noOverstock')}</Typography>
          )}
        </SectionBody>
      </Section>

      <Grid container spacing={2}>
        {canViewItemCost && (
          <Grid size={{ xs: 12, lg: 6 }}>
            <Section title={t('common:dashboard.advanced.conversionImpact')} helpKey="advanced.conversionImpact" chartHeight={220}>
              <SectionBody loading={advancedLoading} height={220}>
                {advanced.conversionImpact.byType.length > 0 ? (
                  <DashboardSizedChart height={220}>
                    <BarChart data={advanced.conversionImpact.byType}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="type" />
                      <YAxis tickFormatter={formatShort} />
                      <Tooltip formatter={(v: number) => formatNum(v, currency)} />
                      <Bar dataKey="value" fill="#7b1fa2" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </DashboardSizedChart>
                ) : (
                  <Typography color="text.secondary">{t('common:dashboard.noData')}</Typography>
                )}
              </SectionBody>
            </Section>
          </Grid>
        )}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Section title={t('common:dashboard.advanced.agentScorecard')} helpKey="advanced.agentScorecard" chartHeight={220} scrollable>
            <SectionBody loading={advancedLoading} height={220}>
              {advanced.agentScorecard.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common:dashboard.salesBreakdown.byAgent')}</TableCell>
                      <TableCell align="right">{t('common:dashboard.kpi.totalSales')}</TableCell>
                      <TableCell align="right">{t('common:dashboard.kpi.totalPayments')}</TableCell>
                      <TableCell align="right">{t('common:dashboard.advanced.debtChange')}</TableCell>
                      <TableCell align="right">{t('common:dashboard.advanced.returnRate')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {advanced.agentScorecard.map((row) => (
                      <TableRow key={row.agent}>
                        <TableCell>{row.agent}</TableCell>
                        <TableCell align="right">{formatShort(row.sales)}</TableCell>
                        <TableCell align="right">{formatShort(row.collections)}</TableCell>
                        <TableCell align="right">{formatShort(row.netDebtChange)}</TableCell>
                        <TableCell align="right">{row.returnRatePct.toFixed(1)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">{t('common:dashboard.noData')}</Typography>
              )}
            </SectionBody>
          </Section>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default AdvancedDashboard;